import { Injectable, Injector } from '@angular/core';

import {
    PermissionCheckerService,
    NotifyService,
    SettingService,
} from 'abp-ng2-module';

import {
    WebShopServiceProxy,
    GetLocalWarehouseExtendedDataOutputDto,
    BasketArticleDto,
    LocalWarehouseExtendedDataDto,
    ArticleExtendedPropertyDto,
    ILocalWarehouseExtendedDataDto,
    LocalWarehouseAmountToChangeListInputDto,
    ILocalWarehouseAmountToChangeListInputDto, IArticleExtendedPropertyDto
} from '@shared/service-proxies/service-proxies';
import {take, tap} from 'rxjs/operators';
import * as _ from 'lodash-es';

import { Observable } from 'rxjs';
import { AppSessionService } from '@axilla/axilla-shared';
import {BehaviorSubject} from '@node_modules/rxjs';
import {finalize, takeUntil} from '@node_modules/rxjs/dist/types/operators';
import {BasePermissionDefinition} from '@shared/permission/base-permission-definition';
import moment from 'moment';

@Injectable({
    providedIn: 'root',
})
export class MLWService {
    private permissionCheckerService: PermissionCheckerService;
    private webShopServiceProxy: WebShopServiceProxy;

    private _isAddMode = false;
    get isAddMode(): boolean {
        return this._isAddMode;
    }
    private set isAddMode(value: boolean) {
        this._isAddMode = value;
    }

    globalConfiguration: ExtendedPropertyType[] = [
        { propertyName: 'THT', fieldType: 'calendar' } as ExtendedPropertyType,
        { propertyName: 'BatchNumber', fieldType: 'text' } as ExtendedPropertyType,
        { propertyName: 'Amount', fieldType: 'number', hidden: true } as ExtendedPropertyType,
    ];

    // Observable for original items
    private _selectedRow$: BehaviorSubject<LocalWarehouseDataRow>;

    // Observable for original items
    private _items$: BehaviorSubject<LocalWarehouseExtendedDataDto[]>;
    // Observable for items which will be displayed
    private _itemsToShow$: BehaviorSubject<LocalWarehouseDataRow[]>;

    constructor(injector: Injector) {
        this.permissionCheckerService = injector.get(PermissionCheckerService);
        this.webShopServiceProxy = injector.get(WebShopServiceProxy);

        this._items$ = new BehaviorSubject<LocalWarehouseExtendedDataDto[]>(undefined);
        this._itemsToShow$ = new BehaviorSubject<LocalWarehouseDataRow[]>(undefined);
        this._selectedRow$ = new BehaviorSubject<LocalWarehouseDataRow>(undefined);
    }

    get isGuided(): boolean {
        const guidedUser = this.permissionCheckerService.isGranted(BasePermissionDefinition.Webshop.View.Guided);
        return guidedUser && !!abp.session.impersonatorUserId;
    }

    reset() {
        this._items$ = new BehaviorSubject<LocalWarehouseExtendedDataDto[]>(undefined);
        this._itemsToShow$ = new BehaviorSubject<LocalWarehouseDataRow[]>(undefined);
        this._selectedRow$ = new BehaviorSubject<LocalWarehouseDataRow>(undefined);

        this.disableAddMode();
    }

    get selectedRow$(): Observable<LocalWarehouseDataRow> {
        return this._selectedRow$.asObservable();
    }
    get selectedRowValue(): LocalWarehouseDataRow {
        return this._selectedRow$.getValue();
    }

    get itemsToShow$(): Observable<LocalWarehouseDataRow[]> {
        return this._itemsToShow$.asObservable();
    }
    get itemsToShowValue(): LocalWarehouseDataRow[] {
        return this._itemsToShow$.getValue();
    }

    private _setItemsToShow(articleInformation: LocalWarehouseDataRow[]) {
        this._itemsToShow$.next(articleInformation);
    }

    get items$(): Observable<LocalWarehouseExtendedDataDto[]> {
        return this._items$.asObservable();
    }
    get itemsValue(): LocalWarehouseExtendedDataDto[] {
        return this._items$.getValue();
    }

    private _setItems(articleInformation: LocalWarehouseExtendedDataDto[]) {
        this._items$.next(articleInformation);
    }

    addNewRow(input: LocalWarehouseDataRow) {
        const row = {
            unspecified: false,
            amount: input.amount,
            extendedProperties: this.convertToArticleExtendedPropertyDto(input)
        } as ILocalWarehouseExtendedDataDto;

        this._addNewRow(row);
    }

    private _addNewRow(input: ILocalWarehouseExtendedDataDto) {
        let data = this.itemsValue;
        const newData = [...data, new LocalWarehouseExtendedDataDto(input)];
        this._setItems(newData);
    }

    convertToArticleExtendedPropertyDto(row: LocalWarehouseDataRow): ArticleExtendedPropertyDto[] | undefined {
        if (!row.tht && !row.batchNumber) {
            return undefined;
        }

        return [
            new ArticleExtendedPropertyDto({
                id: this.generateUniqueId(),
                name: 'THT',
                value: row.tht.toString()
            } as IArticleExtendedPropertyDto),
            new ArticleExtendedPropertyDto({
                id: this.generateUniqueId(),
                name: 'BatchNumber',
                value: row.batchNumber
            } as IArticleExtendedPropertyDto)
        ];
    }

    convertToLocalWarehouseDataRow(row: LocalWarehouseExtendedDataDto[]): LocalWarehouseDataRow[] {
        return this.itemsValue
            .filter(item => !item.unspecified) // Exclude items with unspecified: true
            .map(item => ({
                tht: item.extendedProperties.find(x => x.name === 'THT')?.value ?? '', // Default to empty string if undefined
                batchNumber: item.extendedProperties.find(x => x.name === 'BatchNumber')?.value ?? '',
                amount: item.amount ?? 0 // Default to 0 if undefined
            }));
    }

    getUnspecified(): number {
        if (!this.itemsValue || this.itemsValue?.length === 0) {
            return 0;
        }

        return (this.itemsValue
            .find(item => item.unspecified))?.amount ?? 0;
    }

    // Helper method to generate unique IDs (you can replace it with a UUID generator)
    generateUniqueId(): string {
        return '00000000-0000-0000-0000-000000000000';
//        return Math.random().toString(36).substr(2, 9);
    }

    updateLocalWarehouse(code: string) {
        let userId = abp.session.userId;
        if (this.isGuided) {
            userId = abp.session.impersonatorUserId;
        }

        this.getLocalWarehouseExtendedData$(userId, code)
            .pipe(
                take(1)
            )
            .subscribe();
    }

    getLocalWarehouseExtendedData$(userId: number | undefined, itemCode: string | undefined)
        : Observable<GetLocalWarehouseExtendedDataOutputDto> {
        return this.webShopServiceProxy.getLocalWarehouseExtendedData(userId, itemCode)
            .pipe(
                tap((result: GetLocalWarehouseExtendedDataOutputDto) => {
                    if (result && result?.articleInformation.length > 0) {
                        const articleInformation = result?.articleInformation;
                        this._setItems(articleInformation);

                        this._setItemsToShow(this.convertToLocalWarehouseDataRow(articleInformation));
                    } else {
                        this.reset();
                    }
                })
            );
    }

    updateLocalWarehouseAmount$(input: ILocalWarehouseAmountToChangeListInputDto)
        : Observable<void> {
        return this.webShopServiceProxy
            .updateLocalWarehouseAmount(new LocalWarehouseAmountToChangeListInputDto(input))
            .pipe(
                tap((data: any) => {
                    console.log('test');
                })
            );
    }

    enableAddMode() { this.isAddMode = true; }
    disableAddMode() { this.isAddMode = false; }

    onRowSelect(data: any) {
        this._selectedRow$.next(data);
    }

    onRowUnselect() {
        this._selectedRow$.next(undefined);
    }

    hashLocalWarehouseDataRow(row: LocalWarehouseDataRow): string {
        const data = `${row.tht}|${row.batchNumber}`;
        let hash = 0;
        for (let i = 0; i < data.length; i++) {
            const char = data.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash |= 0; // Convert to 32-bit integer
        }
        return hash.toString();
    }

    updateBasketArticleDto(input: BasketArticleDto, row: LocalWarehouseDataRow) {
        input.extendedData = this.convertToArticleExtendedPropertyDto(row);
        input.hash = this.hashLocalWarehouseDataRow(row);
        input.quantity = input.quantity * -1;
        input.description = this.stringifyLocalWarehouseDataRow(input, row);
    }

    private stringifyLocalWarehouseDataRow(input: BasketArticleDto, row: LocalWarehouseDataRow): string {
        if (!input.description) {
            return `${moment(row.tht).format('DD-MM-YYYY')} - ${row.batchNumber}`;
        }

        return `${moment(row.tht).format('DD-MM-YYYY')} - ${row.batchNumber}\r\n${input.description}`;
    }

}

export class LocalWarehouseDataRow {
    tht!: string;
    batchNumber!: string;
    amount!: number;
}

export class ExtendedPropertyType {
    propertyName: string;
    fieldType: string;
}
